import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

// Deliberate divergence from stock shadcn (gh-555): NO `disabled:pointer-events-none`.
// Disabled write CTAs carry their blocked-action reason in a native `title` tooltip
// (Stories 6.9/13.6), and the browser can only deliver it while the element stays
// hit-testable. Native `disabled` semantics already suppress click/focus, and each
// variant freezes its hover styles under `disabled:hover:*` so the fix is visually
// inert. Do not restore `pointer-events-none` when re-syncing from upstream.
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--focus-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--bg-primary)] disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      variant: {
        primary:
          "bg-[color:var(--bg-brand-solid)] text-[color:var(--text-white)] hover:bg-[color:var(--bg-brand-solid-hover)] disabled:hover:bg-[color:var(--bg-brand-solid)]",
        secondary:
          "border border-[color:var(--border-brand-alt)] bg-transparent text-[color:var(--text-brand-secondary)] hover:bg-[color:var(--bg-brand-primary)] disabled:hover:bg-transparent",
        destructive:
          "bg-[color:var(--bg-error-solid)] text-[color:var(--text-white)] hover:opacity-90 disabled:hover:opacity-50",
        ghost:
          "bg-transparent text-[color:var(--text-primary)] hover:bg-[color:var(--bg-tertiary)] disabled:hover:bg-transparent",
        link:
          "bg-transparent text-[color:var(--text-brand-secondary)] underline-offset-4 hover:underline disabled:hover:no-underline",
      },
      size: {
        default: "min-h-[44px] px-[18px] py-[10px] text-md",
        sm: "min-h-[36px] px-[14px] py-[8px] text-sm",
        lg: "min-h-[48px] px-[22px] py-[12px] text-md",
        icon: "h-10 w-10 p-0",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
